import { Publication, PublicationFileFormat } from "../../types/Publications";
import { TypeUserData } from "../../types/TypeUsers";
import { EducationalHub } from "../../types/EducationalHub";

type LocalizedText = { language: string; text: string };

const getLocalizedText = (values?: LocalizedText[]) =>
  values?.find(({ language }) => language === "en")?.text ??
  values?.[0]?.text ??
  "";

export const getUserLabel = (user: Publication["createdBy"]) => {
  if (!user || typeof user === "string") return "Unknown creator";
  const typedUser = user as TypeUserData;
  const name = typedUser.latinName || getLocalizedText(typedUser.name);
  const surname = typedUser.latinSurname || getLocalizedText(typedUser.surname);
  return [name, surname].filter(Boolean).join(" ") || "Unknown creator";
};

export const getHubLabel = (hub: Publication["educationalHub"]) => {
  if (!hub || typeof hub === "string") return "Unknown Educational Hub";
  return getLocalizedText((hub as EducationalHub).title) || "Unknown Educational Hub";
};

export const getPublisher = (publication: Publication) =>
  publication.educationalHub
    ? { type: "Educational Hub", name: getHubLabel(publication.educationalHub) }
    : { type: "Specialist", name: getUserLabel(publication.createdBy) };

export const getMaterialsLabel = (publication: Publication) => {
  const files = publication.files ?? [];
  const formats = Array.from(new Set(files.map(({ format }) => format))).sort(
    (left) => (left === PublicationFileFormat.PDF ? -1 : 1),
  );
  const formatLabel = formats.length
    ? formats.map((format) => (format === PublicationFileFormat.AUDIO ? "Audio" : "PDF")).join(", ")
    : "No formats";
  return `${formatLabel} · ${files.length} ${files.length === 1 ? "material" : "materials"}`;
};

export const formatRequestDate = (value?: string) => {
  if (!value) return "Request date unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Request date unavailable";
  return new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  }).format(date);
};

export type PublicationLifecycle =
  | "ALL"
  | "DRAFT"
  | "UNDER_REVIEW"
  | "CHANGES_REQUESTED"
  | "APPROVED"
  | "ACTIVE"
  | "DELETED";

export const getPublicationLifecycle = (publication: Publication) => {
  if (publication.deleted) return "Deleted";
  if (publication.isActive) return "Active";
  if (publication.isApproveRequested) return "Under review";
  if (publication.reviewDeclined) return "Changes requested";
  if (publication.isApproved) return "Approved";
  return "Draft";
};

export const getLifecycleFilters = (
  lifecycle: PublicationLifecycle,
): Pick<
  import("../../types/Publications").PublicationFilters,
  "deleted" | "isActive" | "isApproved" | "isApproveRequested" | "reviewDeclined"
> => {
  switch (lifecycle) {
    case "DRAFT":
      return {
        deleted: false,
        isActive: false,
        isApproved: false,
        isApproveRequested: false,
        reviewDeclined: false,
      };
    case "UNDER_REVIEW":
      return { deleted: false, isApproveRequested: true };
    case "CHANGES_REQUESTED":
      return { deleted: false, isApproveRequested: false, reviewDeclined: true };
    case "APPROVED":
      return { deleted: false, isActive: false, isApproved: true };
    case "ACTIVE":
      return { deleted: false, isActive: true };
    case "DELETED":
      return { deleted: true };
    default:
      return {};
  }
};

export const getRelevantTimestamp = (publication: Publication) => {
  if (publication.deleted) return { label: "Updated", value: publication.updatedAt };
  if (publication.isActive) return { label: "Updated", value: publication.updatedAt };
  if (publication.isApproveRequested) {
    return { label: "Requested", value: publication.approveRequestedAt };
  }
  if (publication.reviewDeclined) {
    return { label: "Reviewed", value: publication.reviewedAt ?? publication.updatedAt };
  }
  if (publication.isApproved) {
    return { label: "Approved", value: publication.approvedAt ?? publication.updatedAt };
  }
  return { label: "Created", value: publication.createdAt };
};

export const getFallbackTotal = (
  page: number,
  pageSize: number,
  rowCount: number,
  totalAmount?: number,
) =>
  typeof totalAmount === "number"
    ? totalAmount
    : page * pageSize + rowCount + (rowCount === pageSize ? 1 : 0);
